import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shield, Users, GraduationCap, BookOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { claimFirstAdmin } from "@/lib/admin-setup.functions";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({ meta: [{ title: "Dashboard — Code Cubs Academy" }] }),
  component: SmartRedirect,
});

function SmartRedirect() {
  const { loading, refreshRoles, isAdmin, isParent, isStudent, isInstructor } = useAuth();
  const [claiming, setClaiming] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAdmin) navigate({ to: "/admin", replace: true });
    else if (isInstructor) navigate({ to: "/instructor", replace: true });
    else if (isStudent) navigate({ to: "/student", replace: true });
    else if (isParent) navigate({ to: "/parent", replace: true });
  }, [isAdmin, isInstructor, isStudent, isParent, navigate]);

  const claim = async () => {
    setClaiming(true);
    try {
      const r = await claimFirstAdmin();
      if (r.ok) {
        toast.success("تمت ترقيتك إلى Admin 🎉");
        await refreshRoles();
      } else toast.error("في admin موجود بالفعل");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setClaiming(false);
    }
  };

  if (loading || isAdmin || isInstructor || isStudent || isParent) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // No role yet
  return (
    <div className="container mx-auto px-4 py-12 max-w-2xl">
      <Card className="rounded-3xl border-0 shadow-soft">
        <CardContent className="p-8 text-center">
          <h1 className="text-3xl font-extrabold mb-4">مرحباً بك 👋</h1>
          <p className="text-muted-foreground mb-6">حسابك لسه مفيش role متعين. اختر وجهتك:</p>
          <div className="grid grid-cols-2 gap-3 mb-6">
            <Button asChild variant="outline" className="rounded-2xl h-auto py-4 flex-col gap-2">
              <Link to="/parent">
                <Users className="h-6 w-6" /> Parent
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-2xl h-auto py-4 flex-col gap-2">
              <Link to="/student">
                <GraduationCap className="h-6 w-6" /> Student
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-2xl h-auto py-4 flex-col gap-2">
              <Link to="/instructor">
                <BookOpen className="h-6 w-6" /> Instructor
              </Link>
            </Button>
            <Button
              onClick={claim}
              disabled={claiming}
              variant="outline"
              className="rounded-2xl h-auto py-4 flex-col gap-2"
            >
              {claiming ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                <>
                  <Shield className="h-6 w-6" /> Claim Admin
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            "Claim Admin" يشتغل مرة واحدة فقط — لأول حساب.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
